import { useState } from 'react';
import { Inbox } from 'lucide-react';
import { api } from '../api';
import { useLoad } from '../hooks/useLoad';
import { useOrg } from '../auth/OrgContext';
import { Card, Badge, statusTone, EmptyState } from '../components/ui';

interface EventRow {
  id: string;
  type: string;
  simulation_id: string;
  campaign_id: string;
  occurred_at: string;
}
const TYPES = ['', 'sent', 'opened', 'clicked', 'submitted_data', 'reported', 'bounced', 'replied'];

// Event Log — rendered as a tab under Reporting (no own PageHeader); scoped to the active Org.
export function EventLogSection() {
  const { withOrg, selectedOrgId } = useOrg();
  const [type, setType] = useState('');
  const { data, error } = useLoad(
    () =>
      api.get<{ data: EventRow[] }>(withOrg(`/v1/events?limit=100${type ? `&type=${type}` : ''}`)),
    [type, selectedOrgId],
  );
  const events = data?.data ?? [];

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          className="input w-44 cursor-pointer"
        >
          {TYPES.map((t) => (
            <option key={t} value={t}>
              {t || 'All types'}
            </option>
          ))}
        </select>
      </div>
      {error && (
        <div className="mb-4 rounded-lg border border-line bg-ink/[0.03] px-3 py-2 text-sm text-muted">
          {error}
        </div>
      )}
      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-line text-left text-xs font-medium uppercase tracking-wide text-muted">
            <tr>
              <th className="px-5 py-3">Type</th>
              <th className="px-5 py-3">Simulation</th>
              <th className="px-5 py-3">Campaign</th>
              <th className="px-5 py-3 text-right">When</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {events.map((e) => (
              <tr key={e.id} className="transition-colors hover:bg-ink/[0.02]">
                <td className="px-5 py-3">
                  <Badge tone={statusTone(e.type)}>{e.type}</Badge>
                </td>
                <td className="px-5 py-3 font-mono text-xs text-muted">{e.simulation_id}</td>
                <td className="px-5 py-3 font-mono text-xs text-muted">{e.campaign_id}</td>
                <td className="px-5 py-3 text-right text-xs tabular-nums text-muted">
                  {new Date(e.occurred_at).toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {events.length === 0 && (
          <EmptyState icon={Inbox} title="No events" hint="Try a different filter or Org." />
        )}
      </Card>
    </div>
  );
}
